import type { Domain, Profile, SourceReference } from "./types";

export const cantonPortals = {
  Aargau: "https://www.ag.ch/",
  "Appenzell Ausserrhoden": "https://www.ar.ch/",
  "Appenzell Innerrhoden": "https://www.ai.ch/",
  "Basel-Landschaft": "https://www.bl.ch/",
  "Basel-Stadt": "https://www.bs.ch/",
  Bern: "https://www.be.ch/",
  Fribourg: "https://www.fr.ch/",
  Geneva: "https://www.ge.ch/",
  Glarus: "https://www.gl.ch/",
  Graubünden: "https://www.gr.ch/",
  Jura: "https://www.jura.ch/",
  Lucerne: "https://www.lu.ch/",
  Neuchâtel: "https://www.ne.ch/",
  Nidwalden: "https://www.nw.ch/",
  Obwalden: "https://www.ow.ch/",
  Schaffhausen: "https://www.sh.ch/",
  Schwyz: "https://www.sz.ch/",
  Solothurn: "https://www.so.ch/",
  "St. Gallen": "https://www.sg.ch/",
  Thurgau: "https://www.tg.ch/",
  Ticino: "https://www4.ti.ch/",
  Uri: "https://www.ur.ch/",
  Valais: "https://www.vs.ch/",
  Vaud: "https://www.vd.ch/",
  Zug: "https://www.zg.ch/",
  Zurich: "https://www.zh.ch/",
} as const;

export type CantonName = keyof typeof cantonPortals;
export const cantons = Object.keys(cantonPortals) as CantonName[];

const reviewedAt = "2026-07-29";
export const cantonContextPackVersion = "cantons-2026.07.29";

const directoryUrls = {
  administration: "https://www.ch.ch/en/safety-and-justice/addresses-of-administrative-authorities",
  migration: "https://www.sem.admin.ch/sem/en/home/sem/kontakt/kantonale_behoerden/adressen_kantone_und.html",
  social: "https://www.ahv-iv.ch/en/Contacts/Cantonal-compensation-offices",
  road: "https://asa.ch/strassenverkehrsaemter/adressen/",
  tax: "https://www.ch.ch/en/taxes-and-finances/tax-return/",
  premium: "https://www.priminfo.admin.ch/de/versicherungen/verbilligung",
  employment: "https://www.arbeit.swiss/en/jobseekers/signing-on-and-registration",
} as const;

export type CantonTopic = keyof typeof directoryUrls;

interface CantonMeta {
  code: string;
  languages: string[];
  premiumRegions: 1 | 2 | 3;
}

const cantonMeta: Record<CantonName, CantonMeta> = {
  Aargau: { code: "AG", languages: ["German"], premiumRegions: 1 },
  "Appenzell Ausserrhoden": { code: "AR", languages: ["German"], premiumRegions: 1 },
  "Appenzell Innerrhoden": { code: "AI", languages: ["German"], premiumRegions: 1 },
  "Basel-Landschaft": { code: "BL", languages: ["German"], premiumRegions: 2 },
  "Basel-Stadt": { code: "BS", languages: ["German"], premiumRegions: 1 },
  Bern: { code: "BE", languages: ["German", "French"], premiumRegions: 3 },
  Fribourg: { code: "FR", languages: ["French", "German"], premiumRegions: 2 },
  Geneva: { code: "GE", languages: ["French"], premiumRegions: 1 },
  Glarus: { code: "GL", languages: ["German"], premiumRegions: 1 },
  Graubünden: { code: "GR", languages: ["German", "Romansh", "Italian"], premiumRegions: 3 },
  Jura: { code: "JU", languages: ["French"], premiumRegions: 1 },
  Lucerne: { code: "LU", languages: ["German"], premiumRegions: 3 },
  Neuchâtel: { code: "NE", languages: ["French"], premiumRegions: 1 },
  Nidwalden: { code: "NW", languages: ["German"], premiumRegions: 1 },
  Obwalden: { code: "OW", languages: ["German"], premiumRegions: 1 },
  Schaffhausen: { code: "SH", languages: ["German"], premiumRegions: 2 },
  Schwyz: { code: "SZ", languages: ["German"], premiumRegions: 1 },
  Solothurn: { code: "SO", languages: ["German"], premiumRegions: 1 },
  "St. Gallen": { code: "SG", languages: ["German"], premiumRegions: 3 },
  Thurgau: { code: "TG", languages: ["German"], premiumRegions: 1 },
  Ticino: { code: "TI", languages: ["Italian"], premiumRegions: 2 },
  Uri: { code: "UR", languages: ["German"], premiumRegions: 1 },
  Valais: { code: "VS", languages: ["French", "German"], premiumRegions: 2 },
  Vaud: { code: "VD", languages: ["French"], premiumRegions: 2 },
  Zug: { code: "ZG", languages: ["German"], premiumRegions: 1 },
  Zurich: { code: "ZH", languages: ["German"], premiumRegions: 3 },
};

const taxUrls: Record<CantonName, string> = {
  Aargau: "https://www.ag.ch/de/themen/steuern-finanzen/steuern-startseite",
  "Appenzell Ausserrhoden": "https://ar.ch/verwaltung/departement-finanzen/steuerverwaltung/",
  "Appenzell Innerrhoden": "https://www.ai.ch/themen/steuern",
  "Basel-Landschaft": "https://www.baselland.ch/politik-und-behorden/direktionen/finanz-und-kirchendirektion/steuerverwaltung",
  "Basel-Stadt": "https://www.bs.ch/themen/arbeit-und-steuern/steuererklaerung/steuererklaerung-als-natuerliche-person-einreichen",
  Bern: "https://www.sv.fin.be.ch/de/start.html",
  Fribourg: "https://www.fr.ch/impots/personnes-physiques",
  Geneva: "https://www.ge.ch/remplir-ma-declaration-impot-tant-que-particulier",
  Glarus: "https://www.gl.ch/verwaltung/finanzen-und-gesundheit/online-schalter.html/511/opendcategory/4337",
  Graubünden: "https://www.gr.ch/DE/institutionen/verwaltung/dfg/stv/steuererklaerung/einkommens_und_vermoegenssteuer/Seiten/default.aspx",
  Jura: "https://www.jura.ch/ctr",
  Lucerne: "https://steuern.lu.ch/",
  Neuchâtel: "https://www.ne.ch/autorites/dffi/scco",
  Nidwalden: "https://www.steuern-nw.ch/",
  Obwalden: "https://www.ow.ch/steuern",
  Schaffhausen: "https://www.steuern.sh.ch/",
  Schwyz: "https://www.sz.ch/behoerden/verwaltung/finanzdepartement/steuerverwaltung.html/8756-8758-8802-10332-10354",
  Solothurn: "https://steueramt.so.ch/",
  "St. Gallen": "https://www.sg.ch/steuern-finanzen/steuern.html",
  Thurgau: "https://steuerverwaltung.tg.ch/",
  Ticino: "https://www.ti.ch/sportello/imposte-persone-fisiche",
  Uri: "https://www.ur.ch/finanzen/1570",
  Valais: "https://www.vs.ch/web/scc",
  Vaud: "https://www.vd.ch/etat-droit-finances/impots/impots-pour-les-individus/remplir-ma-declaration-dimpot",
  Zug: "https://zg.ch/de/steuern-finanzen/steuern",
  Zurich: "https://www.zh.ch/de/steuern-finanzen/steuern/steuern-natuerliche-personen/steuererklaerung-natuerliche-personen.html",
};

const premiumUrls: Record<CantonName, string> = {
  Aargau: "https://www.sva-ag.ch/pv",
  "Appenzell Ausserrhoden": "https://www.sovar.ch/",
  "Appenzell Innerrhoden": "https://www.ai.ch/",
  "Basel-Landschaft": "https://www.sva-bl.ch/",
  "Basel-Stadt": "https://www.bs.ch/themen/finanzielle-hilfe/leistungen/praemienverbilligung/praemienverbilligung-beantragen",
  Bern: "https://www.asv.dij.be.ch/",
  Fribourg: "https://www.ecasfr.ch/",
  Geneva: "https://www.ge.ch/organisation/service-assurance-maladie",
  Glarus: "https://www.gl.ch/",
  Graubünden: "https://www.sva.gr.ch/",
  Jura: "https://www.ecasjura.ch/",
  Lucerne: "https://www.was-luzern.ch/ak",
  Neuchâtel: "https://www.ne.ch/autorites/decs/saso/ocab/contact",
  Nidwalden: "https://www.aknw.ch/",
  Obwalden: "https://www.akow.ch/ipv",
  Schaffhausen: "https://www.svash.ch/",
  Schwyz: "https://www.aksz.ch/",
  Solothurn: "https://www.akso.ch/",
  "St. Gallen": "https://www.svasg.ch/ipv",
  Thurgau: "https://gesundheit.tg.ch/",
  Ticino: "https://www.ti.ch/",
  Uri: "https://www.ur.ch/praemienverbilligung",
  Valais: "https://www.avs.vs.ch/",
  Vaud: "https://www.vd.ch/sante-soins-et-handicap/assurance-maladie/subside-a-lassurance-maladie",
  Zug: "https://www.akzug.ch/ipv/",
  Zurich: "https://www.zh.ch/en/gesundheit/praemienverbilligung_krankenversicherung.html",
};

const migrationUrls: Record<CantonName, string> = {
  Aargau: "https://www.ag.ch/de/verwaltung/dvi/migration-integration",
  "Appenzell Ausserrhoden": "https://www.ar.ch/verwaltung/departement-inneres-und-sicherheit/amt-fuer-inneres/",
  "Appenzell Innerrhoden": "https://www.ai.ch/verwaltung/justiz-polizei-und-militaerdepartement/amt-fuer-inneres",
  "Basel-Landschaft": "https://www.baselland.ch/politik-und-behorden/direktionen/sicherheitsdirektion/amt_fuer_migration",
  "Basel-Stadt": "https://www.bs.ch/jsd/bdm/migrationsamt",
  Bern: "https://www.migration.sid.be.ch/de/start.html",
  Fribourg: "https://www.fr.ch/dsjs/spomi",
  Geneva: "https://www.ge.ch/organisation/office-cantonal-population-migrations-ocpm",
  Glarus: "https://www.gl.ch/verwaltung/sicherheit-und-justiz/justiz/migration.html/1215",
  Graubünden: "https://www.gr.ch/DE/institutionen/verwaltung/djsg/afm/ueberuns/Seiten/default.aspx",
  Jura: "https://www.jura.ch/spop",
  Lucerne: "https://migration.lu.ch/",
  Neuchâtel: "https://www.ne.ch/autorites/decs/smig",
  Nidwalden: "https://www.nw.ch/migration/1146",
  Obwalden: "https://www.ow.ch/fachbereiche/1822",
  Schaffhausen: "https://sh.ch/CMS/Webseite/Kanton-Schaffhausen/Beh-rde/Verwaltung/Departement-des-Innern/Migrationsamt-und-Passb-ro-3454-DE.html",
  Schwyz: "https://www.sz.ch/migration",
  Solothurn: "https://www.so.ch/verwaltung/departement-des-innern/migrationsamt/",
  "St. Gallen": "https://www.migrationsamt.sg.ch/",
  Thurgau: "https://migrationsamt.tg.ch/",
  Ticino: "https://www.ti.ch/popolazione",
  Uri: "https://www.ur.ch/unterinstanzen/907",
  Valais: "https://www.vs.ch/web/spm",
  Vaud: "https://www.vd.ch/spop",
  Zug: "https://zg.ch/de/sicherheitsdirektion/amt-fuer-migration",
  Zurich: "https://www.zh.ch/de/sicherheitsdirektion/migrationsamt.html",
};

const roadUrls: Record<CantonName, string> = {
  Aargau: "https://www.ag.ch/de/verwaltung/dvi/strassenverkehr",
  "Appenzell Ausserrhoden": "https://www.ar.ch/verwaltung/departement-inneres-und-sicherheit/strassenverkehrsamt/",
  "Appenzell Innerrhoden": "https://www.ai.ch/verwaltung/justiz-polizei-und-militaerdepartement/strassenverkehrsamt",
  "Basel-Landschaft": "https://www.baselland.ch/politik-und-behorden/direktionen/sicherheitsdirektion/motorfahrzeugkontrolle",
  "Basel-Stadt": "https://www.polizei.bs.ch/verkehr/motorfahrzeugkontrolle",
  Bern: "https://www.svsa.sid.be.ch/de/start.html",
  Fribourg: "https://www.ocn.ch/",
  Geneva: "https://www.ge.ch/organisation/office-cantonal-vehicules-ocv",
  Glarus: "https://www.gl.ch/verwaltung/sicherheit-und-justiz/justiz/strassenverkehrsamt.html/466",
  Graubünden: "https://www.gr.ch/DE/institutionen/verwaltung/djsg/stva/Seiten/Start.aspx",
  Jura: "https://www.jura.ch/ovj",
  Lucerne: "https://strassenverkehrsamt.lu.ch/",
  Neuchâtel: "https://www.scan-ne.ch/",
  Nidwalden: "https://www.vsz.ch/",
  Obwalden: "https://www.vsz.ch/",
  Schaffhausen: "https://sh.ch/CMS/Webseite/Kanton-Schaffhausen/Beh-rde/Verwaltung/Baudepartement/Strassenverkehrs--und-Schifffahrtsamt-3612-DE.html",
  Schwyz: "https://www.sz.ch/behoerden/verwaltung/baudepartement/verkehrsamt.html/8756-8758-8802-9276-9293",
  Solothurn: "https://so.ch/verwaltung/bau-und-justizdepartement/motorfahrzeugkontrolle/",
  "St. Gallen": "https://www.sg.ch/verkehr/strassenverkehr.html",
  Thurgau: "https://strassenverkehrsamt.tg.ch/",
  Ticino: "https://www4.ti.ch/di/sc/home",
  Uri: "https://www.ur.ch/assv",
  Valais: "https://www.vs.ch/de/autos",
  Vaud: "https://www.vd.ch/mobilite/automobile-et-navigation/vehicules/preparer-et-gerer-ses-controles-techniques-expertises",
  Zug: "https://zg.ch/de/sicherheitsdirektion/strassenverkehrsamt",
  Zurich: "https://www.zh.ch/de/sicherheitsdirektion/strassenverkehrsamt.html",
};

interface CantonService {
  label: string;
  url: string;
}

const topicLabels: Record<"tax" | "premium" | "migration" | "road", string> = {
  tax: "Tax return and extensions",
  premium: "Health premium reduction",
  migration: "Permits and migration",
  road: "Vehicle and road office",
};

const cantonServices: Record<CantonName, Partial<Record<CantonTopic, CantonService>>> = Object.fromEntries(
  cantons.map((canton) => [canton, {
    tax: { label: `${canton}: ${topicLabels.tax}`, url: taxUrls[canton] },
    premium: { label: `${canton}: ${topicLabels.premium}`, url: premiumUrls[canton] },
    migration: { label: `${canton}: ${topicLabels.migration}`, url: migrationUrls[canton] },
    road: { label: `${canton}: ${topicLabels.road}`, url: roadUrls[canton] },
  }]),
) as Record<CantonName, Partial<Record<CantonTopic, CantonService>>>;

export interface CantonDeskService {
  topic: "administration" | "tax" | "premium" | "migration" | "road";
  label: string;
  summary: string;
  url: string;
  authority: string;
  reviewedAt: string;
  verify: true;
}

export interface CantonContextDefinition {
  name: CantonName;
  code: string;
  officialLanguages: string[];
  premiumRegions: 1 | 2 | 3;
  communeAffectsPremiumRegion: boolean;
  portal: string;
  reviewedAt: string;
  services: CantonDeskService[];
}

const topicSummaries: Record<CantonDeskService["topic"], string> = {
  administration: "Official starting point for cantonal services and current procedures.",
  tax: "Check the current return, filing deadline and extension route for individuals.",
  premium: "Check eligibility, whether an application is needed and the current cantonal process.",
  migration: "Use for residence permits, renewals, settlement and migration-office questions.",
  road: "Use for registration, inspections, licence matters and vehicle administration.",
};

export const cantonPackSources = [
  {
    label: "Cantonal migration authorities",
    authority: "State Secretariat for Migration",
    url: directoryUrls.migration,
    reviewedAt,
  },
  {
    label: "Cantonal road traffic offices",
    authority: "Association of Road Traffic Offices",
    url: directoryUrls.road,
    reviewedAt,
  },
  {
    label: "Cantonal premium-reduction offices",
    authority: "Federal Office of Public Health / Priminfo",
    url: directoryUrls.premium,
    reviewedAt,
  },
  {
    label: "Tax return overview",
    authority: "Swiss Confederation",
    url: directoryUrls.tax,
    reviewedAt,
  },
] as const;

export function cantonContext(
  canton: string | undefined,
): CantonContextDefinition | null {
  if (!canton || !(canton in cantonPortals)) return null;
  const name = canton as CantonName;
  const meta = cantonMeta[name];
  const serviceTopics: Array<"tax" | "premium" | "migration" | "road"> = [
    "tax",
    "premium",
    "migration",
    "road",
  ];
  const services: CantonDeskService[] = [
    {
      topic: "administration",
      label: `${name} official portal`,
      summary: topicSummaries.administration,
      url: cantonPortals[name],
      authority: `Canton of ${name}`,
      reviewedAt,
      verify: true,
    },
    ...serviceTopics.map((topic) => ({
      topic,
      label: topicLabels[topic],
      summary: topicSummaries[topic],
      url: cantonServices[name][topic]!.url,
      authority: `Canton of ${name}`,
      reviewedAt,
      verify: true as const,
    })),
  ];
  return {
    name,
    code: meta.code,
    officialLanguages: meta.languages,
    premiumRegions: meta.premiumRegions,
    communeAffectsPremiumRegion: meta.premiumRegions > 1,
    portal: cantonPortals[name],
    reviewedAt,
    services,
  };
}

const domainTopics: Partial<Record<Domain, CantonDeskService["topic"]>> = {
  Tax: "tax",
  Health: "premium",
  Residency: "migration",
  Belonging: "migration",
  Mobility: "road",
};

export function relevantCantonServices(
  profile: Profile,
  domains: Domain[] = [],
): CantonDeskService[] {
  const context = cantonContext(profile.canton);
  if (!context) return [];
  const preferred: CantonDeskService["topic"][] = [];
  const add = (topic: CantonDeskService["topic"]) => {
    if (!preferred.includes(topic)) preferred.push(topic);
  };

  domains.forEach((domain) => {
    const topic = domainTopics[domain];
    if (topic) add(topic);
  });
  if (profile.taxMode) add("tax");
  add("premium");
  if (profile.residency && profile.residency !== "swiss") add("migration");
  if (
    (profile.vehicle && profile.vehicle !== "none")
    || profile.vehicles?.some((vehicle) => vehicle.kind === "car" || vehicle.kind === "motorbike")
  ) add("road");
  add("administration");
  ["tax", "migration", "road"].forEach((topic) => add(topic as CantonDeskService["topic"]));

  return preferred
    .map((topic) => context.services.find((service) => service.topic === topic))
    .filter((service): service is CantonDeskService => Boolean(service));
}

export function cantonService(
  canton: string | undefined,
  topic: CantonTopic,
): (CantonService & { authority: string }) | null {
  if (!canton || !(canton in cantonPortals)) return null;
  const name = canton as CantonName;
  const service = cantonServices[name]?.[topic];
  return service ? { ...service, authority: `Canton of ${name}` } : null;
}

export function cantonAuthoritySources(
  canton: string | undefined,
  topic: CantonTopic = "administration",
): SourceReference[] {
  if (!canton || !(canton in cantonPortals)) {
    return [{
      label: "Swiss cantonal-authority directory",
      authority: "Swiss Confederation",
      url: directoryUrls[topic],
      jurisdiction: "cantonal",
      reviewedAt,
      verify: true,
    }];
  }
  const name = canton as CantonName;
  const direct = cantonService(name, topic);
  return [
    ...(direct ? [{
      label: direct.label,
      authority: direct.authority,
      url: direct.url,
      jurisdiction: "cantonal" as const,
      reviewedAt,
      verify: true,
    }] : []),
    {
      label: `${name} official portal`,
      authority: `Canton of ${name}`,
      url: cantonPortals[name],
      jurisdiction: "cantonal",
      reviewedAt,
      verify: true,
    },
    {
      label: `${topic === "social" ? "Compensation-office" : topic} authority directory`,
      authority: "Swiss Confederation",
      url: directoryUrls[topic],
      jurisdiction: "cantonal",
      reviewedAt,
      verify: true,
    },
  ];
}
